import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// INVITING A PLAYER TO THE CLUB, BY E-MAIL (#710, #1061).
//
// `POST /members/:id/invitation`. The request names the player and an
// address; which club they are invited to is the server's answer, from the
// caller's resolved scope.
//
// WHAT COMES BACK IS «SENT», NEVER A TOKEN. The link going to the invited
// mailbox is the first of two proofs, so the operator gets no copy of it.
//
// NOBODY IS ADDED TO THE ROSTER HERE. For a player who is not already a
// member, the roster row is written when they accept from their own mailbox
// (#197 Q4). The join code this replaced (`/invite`) put anybody on the roster
// at once; it is retired.
// ─────────────────────────────────────────────────────────────────────

export interface MemberInvitationSent {
  /** Whether the mail provider accepted it. False is an outcome, not an error. */
  readonly sent: boolean;
  readonly expiresAt: string;
  /** 'roster': a member's account claim. 'consent': an invitation to join. */
  readonly kind: 'roster' | 'consent';
  readonly club: { readonly name: string | null };
}

export type MemberInvitationResult =
  | { readonly state: 'ok'; readonly invitation: MemberInvitationSent }
  /** The server said no, with a code. Codes are never translated on the wire (#519). */
  | { readonly state: 'refused'; readonly reason: string }
  | { readonly state: 'error'; readonly status: number | null };

export async function sendMemberInvitation(
  playerId: string,
  email: string,
): Promise<MemberInvitationResult> {
  let res: Response;
  try {
    res = await apiFetch(`/members/${encodeURIComponent(playerId)}/invitation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `grantsMembership`: «Inviter til spiller-appen» invites somebody to be
      // a member. For a player not yet on the roster the server grants it
      // whatever this says; for a roster player it is the club's choice, and
      // this button's answer is yes.
      body: JSON.stringify({ email: email.trim(), grantsMembership: true }),
    });
  } catch {
    return { state: 'error', status: null };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.ok) {
    if (typeof body?.sent !== 'boolean') return { state: 'error', status: res.status };
    const club = (body.club ?? {}) as Record<string, unknown>;
    return {
      state: 'ok',
      invitation: {
        sent: body.sent,
        expiresAt: typeof body.expiresAt === 'string' ? body.expiresAt : '',
        kind: body.kind === 'consent' ? 'consent' : 'roster',
        club: { name: typeof club.name === 'string' ? club.name : null },
      },
    };
  }
  const reason = typeof body?.error === 'string' ? body.error : null;
  return reason === null ? { state: 'error', status: res.status } : { state: 'refused', reason };
}
